import _ from "lodash";
import getDayOfYear from "date-fns/getDayOfYear";
import getWeekOfMonth from "date-fns/getWeekOfMonth";
import getWeek from "date-fns/getWeek";
import dateFormat from "date-fns/format";
import { formatInTimeZone } from "date-fns-tz";
import xml from "csi/xml";
import csi from "csi/main";
import reflection from "csi/reflection";
import { C8AttributeValue, C8Node, cnl, ICache } from "csi/typings";
import types from "csi/types";
import resolver from "csi/resolver";
import Processor from "csi/middleware/rollup/Processor";
import sessionConstants from "csi/sessionConstants";
import { trimmedString } from "csi/utility/stringUtility";
import { numberTruncate, formatNumberWithStylePattern } from "csi/utility/numberUtility";
import { isNullOrUndefined } from "csi/utility/coreUtility";

const RE_FORMAT = /\{([^\}]+)\}/g;
const DATE_FORMAT_STYLES = ["short", "medium", "long", "full"];
const TYPE_NUMBER = "number";
const NUMBER_FORMAT_STYLES = ["integer", "currency", "percent"];
const TYPE_DATE = "date";
const TYPE_TIME = "time";
const UNIQUE_KEY = "###";
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const SUNDAY = 1;
const MONDAY = 2;
const FRIDAY = 6;
const SATURDAY = 7;
type TimeValue = string | number | Date;
type DateTimeStyle = "short" | "medium" | "long" | "full";
let serverTimeZoneOffset = 0;

export interface IEvaluateParam {
	forceLoadMissingNodes: boolean;
}
/**
 * Evalue expression using the context. 
 * TS does not allow the use of the 'with' in strict mode so added this function outside the class
 */
function evalWithContext(ctx: ServerRollupScope, expr: string): any {
	return eval(`with (ctx) ${expr}`);
}

/**
 * Converts js Arguments to array
 */
function _argumentsToArray(args: IArguments): any[] {
	var a = [];
	var n = args.length;

	for (var i = 0; i < n; ++i) {
		if (i || (typeof args[0] !== "string") || (args[0].indexOf("class:") != 0)) {
			a = a.concat(args[i]);
		}
	}
	return a;
}

class ServerRollupScope {
	public rp: Processor = null;

	protected params: IEvaluateParam = null;
	protected activeNode: C8Node = null;
	protected _currentNode: C8Node = null;
	private readonly cache: ICache = null;
	private readonly variables: Record<string, C8AttributeValue> = null;
	protected readonly forceLoadMissingNodes: boolean;
	private readonly value: C8AttributeValue = null;

	constructor(
		rp: Processor,
		node: C8Node,
		cache: ICache,
		value?: C8AttributeValue,
		params?: IEvaluateParam,
	) {
		this.activeNode = this._currentNode = node;
		this.cache = cache;
		this.params = params;
		this.rp = rp;

		if (value != null) {
			this.value = value;
			this.variables = { value };
		} else {
			this.variables = {};
		}

		this.forceLoadMissingNodes = params?.forceLoadMissingNodes || false;
	}
	// Array functions

	//SERVER:Array:add
	add(container: any, index_value: any, value?: any): any {
		if (value === undefined) {
			if (_.isArray(container)) {
				let x: any;

				if (_.isArray(index_value)) {
					// the index parameter is an array: indicating it is the add(Array, Array) case
					x = index_value;
				} else if (_.isObject(index_value)) {
					// the index parameter is an object: indicating it is the add(Array, Object) case
					// TODO: Do we need to check that the values within index_value are singular and not full objects/arrays?
					x = _.values(index_value);
				} else if (index_value != null) {
					// the index parameter is a primitive value: add(Array, Primitive)
					x = [index_value];
				}

				if (x !== undefined) {
					Array.prototype.push.apply(container, x);
				}
			} else if (_.isObject(container) && !_.isArray(index_value) && _.isObject(index_value)) {
				// add(Object, Object) - the only case that is acceptable
				for (const key in index_value) {
					container[key] = index_value[key];
				}
			} else {
				throw new EvalError();
			}
		} else {
			if (index_value == null || _.isObject(index_value)) {
				// sanitize index_value
				throw new EvalError();
			}

			if (_.isObject(value)) {
				// sanitize value
				throw new EvalError();
			}

			if (_.isArray(container)) {
				// add(array, primitive, primitive)
				container.splice(index_value, 0, value);
			} else if (_.isObject(container)) {
				// add(object, primitive, primitive)
				container[index_value] = value;
			} else {
				throw new EvalError();
			}
		}
		return container;
	}

	//SERVER:Array:at
	at(a: string | any[], n: any): any {
		if (!a) return null;

		// if string, then "a" is most likely a CNL and "n" is an attribute of the node
		if (_.isString(a)) return this.attr(a, n);

		// otherwise, "a" is array or map and "n" is an index
		return a[n];
	}

	//SERVER:Array:contains
	contains(a: any[], v: any): boolean {
		return a ? (_.indexOf(a, v) >= 0) : false;
	}

	//SERVER:Array:first
	first(a: any[]): any {
		return a ? _.first(a) : null;
	}

	//SERVER:Array:indexOf
	indexOf(a: any[], v: any): number {
		return a ? _.indexOf(a, v) : -1;
	}

	//SERVER:Array:isEmpty
	isEmpty(a: any[]): boolean {
		return _.isEmpty(a);
	}

	//SERVER:Array:keys
	keys(container: any): any[] {
		const result = [];
		let i: any;

		if (container instanceof Array) {
			for (i = 0; i < container.length; ++i)
				result.push(i);
		} else {
			for (i in container)
				result.push(i);
		}
		return result;
	}

	//SERVER:Array:last
	last(a: any[]): any {
		return _.last(a);
	}

	//SERVER:Array:order
	order(a: any[], expr: any): any {
		if (!a) return a;
		if (!expr) { return a.sort(); }

		// do not currently support variant to order child nodes
		return null;
	}

	//SERVER:Array:range
	range(a: any[], n1: number, n2: number): any[] {
		if (!a) {
			return a;
		}

		const last = a.length;

		if (n1 < 0) {
			n1 = last + n1;
		}

		if (n2 < 0) {
			n2 = last + n2;
		}

		return a.slice(n1, n2 + 1);
	}

	//SERVER:Array:size
	size(container: any): number {
		if (container == null) {
			return 0;
		}

		return _.isObject(container) ? _.size(container) : 1;
	}

	//SERVER:Array:unique
	unique(a: any[]): any[] {
		return _.uniq(a);
	}

	// Condition functions

	//SERVER:Condition:check
	check(cond: number, caseNeg: any, caseZero: any, casePos: any): any {
		if (cond < 0) {
			return caseNeg;
		} else if (cond > 0) {
			return casePos;
		}

		return caseZero;
	}

	//SERVER:Condition:if
	ifthenelse(cond: any, caseTrue: any, caseFalse: any): any {
		// summary: support for if statements
		return cond ? caseTrue : caseFalse;
	}

	//SERVER:Condition:ifNaN
	ifNaN(x: any, caseNaN: any): any {
		const n = Number(x);
		return (isNaN(n) || (n === Infinity) || (n === -Infinity)) ? caseNaN : n;
	}

	//SERVER:Condition:ifNull
	ifNull(x: any, caseNull: any): any {
		return (x == null || x == csi.EMPTY_URL) ? caseNull : x;
	}

	//Date functions

	//SERVER:Date:addDuration
	addDuration(time: number, days: number): number {
		// reference http://javascript.about.com/library/blbusdayadd.htm
		if (days < 0) throw new Error("Cannot execute addDuration with negative days");

		const wks = Math.floor(days / 5);
		const dys = days % 5;

		const dt = types.getTimeDate(time);
		let dayOfMonth = dt.getDate();
		let dayOfWeek = dt.getDay();

		// if start day is Saturday or Sunday, then move to Monday
		if (dayOfWeek === 6) {
			dayOfMonth += 2;
			dayOfWeek = 1;
		} else if (dayOfWeek === 0) {
			dayOfMonth += 1;
			dayOfWeek = 1;
		}

		// for example, if on Friday and adding 1 day, really need to add 3
		if (dayOfWeek + dys > 5) {
			dayOfMonth += 2;
		}

		dt.setDate(dayOfMonth + wks * 7 + dys);

		return types.getDateTime(dt);
	}

	//SERVER:Date:dateAmPm
	dateAmPm(time: TimeValue): boolean {
		return new Date(time)
			.getHours() < 11;
	}

	//SERVER:Date:dateDate
	dateDate(time: TimeValue): number {
		return new Date(time)
			.getDate();
	}

	//SERVER:Date:dateDay
	dateDay(time: TimeValue): number {
		return new Date(time)
			.getDay() + 1;
	}

	//SERVER:Date:dateDayOfYear
	dateDayOfYear(time: TimeValue): number {
		return getDayOfYear(new Date(time));
	}

	//SERVER:Date:dateDSTOffset
	dateDSTOffset(time: TimeValue): number {
		const date = new Date(time);
		const jan = new Date(date.getFullYear(), 0, 1);
		const jul = new Date(date.getFullYear(), 6, 1);
		const stdTimeZoneOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
		return (stdTimeZoneOffset - date.getTimezoneOffset()) * 60 * 1000;
	}

	//SERVER:Date:dateEra
	dateEra(time: TimeValue): number {
		const eraDateTimeFormat = new Intl.DateTimeFormat("en", { era: "short" });
		const era = eraDateTimeFormat.formatToParts(new Date())[6].value;
		return era === "AD" ? 1 : 0;
	}

	//SERVER:Date:dateFromISO8601
	dateFromISO8601(isoDate: string): Date {
		if (!isoDate) {
			return null;
		}
		return new Date(isoDate);
	}

	//SERVER:Date:dateToISO8601
	dateToISO8601(time: TimeValue, timeZone?: string): string {
		// supported TimeZone string formats are for example "Europe/Copenhagen", "PST"
		// Unlike in Java: GMT+2:00 is not supported
		if (!time) {
			return null;
		}
		if (!timeZone) {
			timeZone = "UTC";
		}
		return formatInTimeZone(time, timeZone, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
	}

	//SERVER:Date:dateHour
	dateHour(time: TimeValue): number {
		const h = new Date(time)
			.getHours() + 1; return (h > 12) ? (h - 12) : h;
	}

	//SERVER:Date:dateHourOfDay
	dateHourOfDay(time: TimeValue): number {
		return new Date(time)
			.getHours() + 1;
	}

	//SERVER:Date:dateMillisecond
	dateMillisecond(time: TimeValue): number {
		return new Date(time)
			.getMilliseconds();
	}

	//SERVER:Date:dateMinute
	dateMinute(time: TimeValue): number {
		return new Date(time)
			.getMinutes();
	}

	//SERVER:Date:dateMonth
	dateMonth(time: TimeValue): number {
		return new Date(time)
			.getMonth();
	}

	//SERVER:Date:dateSecond
	dateSecond(time: TimeValue): number {
		return new Date(time)
			.getSeconds();
	}

	//SERVER:Date:dateWeekOfMonth
	dateWeekOfMonth(time: TimeValue): number {
		return getWeekOfMonth(new Date(time));
	}

	//SERVER:Date:dateWeekOfYear
	dateWeekOfYear(time: TimeValue): number {
		return getWeek(new Date(time));
	}

	//SERVER:Date:dateYear
	dateYear(time: TimeValue): number {
		return new Date(time)
			.getFullYear();
	}

	//SERVER:Date:dateZoneOffset
	dateZoneOffset(time: TimeValue): number {
		return new Date(time)
			.getTimezoneOffset() * 60 * 1000;
	}

	//SERVER:Date:days
	days(n: number): number {
		return n * 24 * 60 * 60 * 1000;
	}

	//SERVER:Date:duration
	duration(time1: number, time2: number): number {
		// summary: logic duplicated from Java version.
		let dDateBegin;
		let dDateEnd;
		const timeDifference = serverTimeZoneOffset;

		// server time offset should be added to UTC timezone.
		// So we have to get UTC date for the given epoch time. Then we add timeDifference. This gives us Date in server time zone
		dDateBegin = types.getTimeDate(Math.min(time1, time2) + timeDifference);
		dDateEnd = types.getTimeDate(Math.max(time1, time2) + timeDifference);

		let nBusinessDays = 0;

		// get days of the week as in Java (1-Sunday, 2-Monday, ..., 7-Saturday)
		var nDayOfWeekBegin = dDateBegin.getUTCDay() + 1;
		var nDayOfWeekEnd = dDateEnd.getUTCDay() + 1;

		// same week
		if (nDayOfWeekBegin <= nDayOfWeekEnd && (dDateEnd.getTime() - dDateBegin.getTime() < MILLISECONDS_PER_DAY * 6)) {
			if ((nDayOfWeekEnd != SUNDAY) && (nDayOfWeekBegin != SATURDAY)) {
				nBusinessDays = Math.min(nDayOfWeekEnd, FRIDAY) - Math.max(nDayOfWeekBegin, MONDAY) + 1;
			}
		} else {
			let nDaysToAdd: number;

			if (nDayOfWeekBegin != SATURDAY && nDayOfWeekBegin != SUNDAY) {
				nDaysToAdd = FRIDAY - nDayOfWeekBegin + 1;

				// Shift to the nearest Saturday Forward
				dDateBegin = new Date(dDateBegin.getTime() + nDaysToAdd * MILLISECONDS_PER_DAY);
				nBusinessDays += nDaysToAdd;
			}
			if (nDayOfWeekEnd != SATURDAY && nDayOfWeekEnd != SUNDAY) {
				nDaysToAdd = nDayOfWeekEnd - MONDAY + 1;

				// Shift to the nearest Sunday Backward
				dDateEnd = new Date(dDateEnd.getTime() - nDaysToAdd * MILLISECONDS_PER_DAY);
				nBusinessDays += nDaysToAdd;
			}

			// Calculate remaining complete weeks. 3 days is to remove any miss-alignment at the weekends dates
			nBusinessDays += 5 * Math.floor((dDateEnd.getTime() - dDateBegin.getTime() + 3 * MILLISECONDS_PER_DAY) / (7 * MILLISECONDS_PER_DAY));
		}

		return nBusinessDays;
	}

	//SERVER:Date:hours
	hours(n: number): number {
		return n * 60 * 60 * 1000;
	}

	//SERVER:Date:minutes
	minutes(n: number): number {
		return n * 60 * 1000;
	}

	//SERVER:Date:nowTime
	nowTime(): number {
		return this.rp.timeCurrent;
	}

	//SERVER:Date:rollupTime
	rollupTime(): number {
		return this.rp.timeCurrent;
	}

	//SERVER:Date:weeks
	weeks(n: number): number {
		return n * 7 * 24 * 60 * 60 * 1000;
	}

	//Math functions

	//SERVER:Math:abs
	abs(x: number): number {
		return Math.abs(x);
	}

	//SERVER:Math:acos
	acos(x: number): number {
		return Math.acos(x);
	}

	//SERVER:Math:asin
	asin(x: number): number {
		return Math.asin(x);
	}

	//SERVER:Math:asinh
	asinh(x: number): number {
		return Math.asinh(x);
	}

	//SERVER:Math:atan
	atan(x: number): number {
		return Math.atan(x);
	}

	//SERVER:Math:atan2
	atan2(x: number, y: number): number {
		return Math.atan2(x, y);
	}

	//SERVER:Math:atanh
	atanh(x: number): number {
		return Math.atanh(x);
	}

	//SERVER:Math:ceil
	ceil(x: number, pre: number): number {
		pre = pre || 1;
		if (!pre || pre == Infinity) { // NaN, Infinity, 0, null, undefined
			return NaN;
		}
		return Math.ceil(x / pre) * pre;
	}

	//SERVER:Math:cos
	cos(x: number): number {
		return Math.cos(x);
	}

	//SERVER:Math:cosh
	cosh(x: number): number {
		return Math.cosh(x);
	}

	//SERVER:Math:exp
	exp(x: number): number {
		return Math.exp(x);
	}

	//SERVER:Math:floor
	floor(x: number, pre: number): number {
		pre = pre || 1;
		if (!pre || pre == Infinity) { // NaN, Infinity, 0, null, undefined
			return NaN;
		}

		return Math.floor(x / pre) * pre;
	}

	//SERVER:Math:inRange
	inRange(x: number, n1: number, n2: number): number {
		return (x < n1) ? -1 : ((x > n2) ? 1 : 0);
	}

	//SERVER:Math:ln
	ln(x: number): number {
		return Math.log(x);
	}

	//SERVER:Math:log
	log(x: number): number {
		return Math.log10(x);
	}

	//SERVER:Math:rand
	rand(): number {
		return Math.random();
	}

	//SERVER:Math:round
	round(x: number, pre: number): number {
		return numberTruncate(x, pre);
	}

	//SERVER:Math:sin
	sin(x: number): number {
		return Math.sin(x);
	}

	//SERVER:Math:sinh
	sinh(x: number): number {
		return Math.sinh(x);
	}

	//SERVER:Math:sqrt
	sqrt(x: number): number {
		return Math.sqrt(x);
	}

	//SERVER:Math:tan
	tan(x: number): number {
		return Math.tan(x);
	}

	//SERVER:Math:tanh
	tanh(x: number): number {
		return Math.tanh(x);
	}

	//SERVER:Math:uniqueKey
	uniqueKey(): string {
		return UNIQUE_KEY;
	}

	//Node functions

	//SERVER:Node:attr
	attr(url: string, idAttr?: string): any {
		// handle both cases where node is given or not - if not, then expression is the first parameter
		const node = idAttr ? this._getNode(url) : this.activeNode;
		if (!node) return null;

		// ask rollup processor for evaluated value
		const x = this.rp.evaluateAttribute(idAttr || url, node, true, this.params);
		if (x === null) {
			throw new EvalError();
		}

		return x;
	}

	//SERVER:Node:attrExists
	attrExists(url: string, idAttr: string): boolean {
		const node = idAttr ? this._getNode(url) : this.activeNode;
		return xml.nodeHasAttribute(node, idAttr || url);
	}

	//SERVER:Node:currentUser
	currentUser(): cnl {
		return sessionConstants.userUrl;
	}

	//SERVER:Node:eval
	_evalAttr(evalNodeCtxUrl: string, nodeWithAttrUrl: string, attrId: string): any {
		const nodeWithAttr = this._getNode(nodeWithAttrUrl);
		const evalNodeCtx = this._getNode(evalNodeCtxUrl);

		if (!nodeWithAttr || !evalNodeCtx) {
			throw new Error("Nodes " + evalNodeCtxUrl + " and " + nodeWithAttrUrl + " do not exist.");
		}

		// in some cases it appears that nodes exported to the reflection cache may not contain attribute types, so assume those are of the correct type
		const attrType = xml.nodeAttributeGetType(nodeWithAttr, attrId);
		if (attrType && (attrType != xml.ATTR_EXPRESSION)) {
			throw new Error("The Value Type of attrId " + attrId + " in node " + nodeWithAttrUrl + " is not expression.");
		}

		const node = this.activeNode;
		const expression = nodeWithAttr[attrId];

		try {
			this.activeNode = evalNodeCtx;
			return evalWithContext(this, expression);
		} finally {
			this.activeNode = node;
		}
	}

	//SERVER:Node:thisNode
	thisNode(): cnl {
		return this.activeNode?.$URL ?? csi.EMPTY_URL;
	}

	//Operators functions

	//SERVER:Operators:plus
	plus(left: any, right: any): any {
		if (left === null || left === undefined)
			return right;
		if (right === null || right === undefined)
			return left;

		let a = [];

		if (left instanceof Array || right instanceof Array) {
			a = [];
			a = a.concat(left);
			a = a.concat(right);
			return a;
		}

		if (xml.isValidURL(left) && xml.isValidURL(right)) {
			a = [];
			a.push(left);
			a.push(right);
			return a;
		}

		return left + right;
	}

	//SERVER:Operators:minus
	minus(left: any, right: any): any {
		if (left === null || left === undefined || right === null || right === undefined)
			return left;

		if (left instanceof Array) {
			const container = [].concat(left);
			const what = right instanceof Array ? right : [right];
			what.forEach((elem) => {
				const index = container.indexOf(elem);
				if (index >= 0)
					container.splice(index, 1);
			});
			return container;
		}

		return left - right;
	}

	//rollup functions

	//SERVER:Rollup:average
	_average(_a: any[], func?: Function): number {
		let _total = 0;
		let _count = 0;
		this._rollup(_a, (_value: any) => {
			if (func == null)
				_total += _value;
			else
				_total += func();
			++_count;
		});
		return _count == 0 ? 0 : _total / _count;
	}

	//SERVER:Rollup:block
	block(): any {
		if (arguments.length <= 0)
			return null;
		return arguments[arguments.length - 1];
	}

	//SERVER:Rollup:common
	_common(_a: any[], func?: Function): any[] {
		let _total = new Object();
		let _count = 0;

		this._rollup(_a, (_value: any) => {
			let i: any;

			if (func != null)
				_value = func();
			if (_value == null)
				return;
			if ((typeof _value) == "string" && !xml.isValidURL(_value))
				_value = this.attr(_value);
			if ((typeof _value) != "object")
				_value = [_value];

			if (_count++ == 0) {
				for (i in _value)
					_total[_value[i]] = true;
			} else {
				var _newtotal = new Object();
				for (i in _value)
					if (_total[_value[i]])
						_newtotal[_value[i]] = true;
				_total = _newtotal;
			}
		});
		const _result = [];
		for (var i in _total)
			_result.push(i);

		return _result;
	}

	//SERVER:Rollup:earliest
	earliest(a: any[], id?: string): any {
		return this.min(a, id);
	}

	//SERVER:Rollup:filter
	_filter(_a: any[], func: Function): any[] {
		const self = this;
		const _result = [];
		this._rollup(_a, (_value: any) => {
			if (func())
				_result.push(self.activeNode.$URL);
		});
		return _result;
	}

	//SERVER:Rollup:foreach
	_foreach(_a: any[], func: Function): any {
		let ret: any;
		_a.forEach((x) => {
			ret = func(x);
		});
		return ret;
	}

	//SERVER:Rollup:join
	_join(_a: any[], func?: Function): any {
		const _total = [];
		const self = this;
		this._rollup(_a, (_value: any) => {
			if (func != null) {
				_value = func();
			}
			self.add(_total, _value);
		});
		return _total;
	}

	join(a: any[], id?: string): any[] {
		return this._getValues(a, id);
	}


	//SERVER:Rollup:latest
	latest(a: any[], id?: string): any {
		return this.max(a, id);
	}

	//SERVER:Rollup:max
	_max(_a: any[], func?: Function): any {
		let _best = null;
		this._rollup(_a, (_value: any) => {
			if (func != null)
				_value = func();
			if (_best === null || _best < _value)
				_best = _value;
		});
		return _best;
	}

	max(a: any[], id?: string): any {
		const values = this._getValues(a, id);
		let x = 0;

		values.forEach((n: any, i: number) => {
			if (!i || (n > x)) {
				x = n;
			}
		});

		return x;
	}

	//SERVER:Rollup:min
	_min(_a: any[], func?: Function): any {
		let _best = null;
		this._rollup(_a, (_value: any) => {
			if (func != null)
				_value = func();
			if (_best === null || _best > _value)
				_best = _value;
		});
		return _best;
	}

	min(a: any[], id?: string): any {
		const values = this._getValues(a, id);
		let x = 0;
		values.forEach((n: any, i: number) => {
			if (!i || (n < x)) {
				x = n;
			}
		});
		return x;
	}

	//SERVER:Rollup:mult
	_mult(_a: any[], func?: Function): number {
		let _total = 1;
		this._rollup(_a, (_value: any) => {
			if (func == null)
				_total *= _value;
			else
				_total *= func();
		});
		return _total;
	}

	//SERVER:Rollup:sum
	_sum(_a: any[] | any, func?: Function): number {
		let _total = 0;
		this._rollup(_a, (_value: any) => {
			if (func == null)
				_total += _value;
			else
				_total += func();
		});
		return _total;
	}

	_rollup(a: string | number | object | any[], func: Function): void {
		if ((typeof a) == "string") {
			if (xml.isValidURL(a as string)) {
				a = [a];
			} else {
				a = this.attr(a as string);
			}
		}

		const prev = this.activeNode;
		try {
			if (_.isObject(a)) {
				a = _.values(a);
			} else if (_.isNumber(a)) {
				a = func(a);
			}

			(a as any[]).forEach((_value) => {
				if (xml.isValidURL(_value)) {
					this.activeNode = this._getNode(_value);
				} else if (_value == null || _value == csi.EMPTY_URL) {
					return;
				}

				func(_value);
			});
		} finally {
			this.activeNode = prev;
		}
	}

	//String functions

	//SERVER:String:combine
	combine(strings: any[], separator: string): string {
		return strings.join(separator);
	}

	//SERVER:String:find
	find(s: string, sub: string): number {
		return s.indexOf(sub);
	}

	//SERVER:String:format
	format(...args: any[]): any {
		// summary: basic support for java.text.MessageFormat
		const { siteLocale } = sessionConstants;

		return args[0].replace(RE_FORMAT, (match: any, formatElement: any) => {
			const format = formatElement.split(",");
			const index = Number(format[0]) + 1;
			let value = args[index];
			if (value != null) {
				// format value based on format type and format style
				const type = trimmedString(format[1]);
				const style = trimmedString(format[2]);

				if (type == TYPE_NUMBER) {
					let bHash = false;
					let pattern: string;
					let type: string;
					if (style != null) {
						if (NUMBER_FORMAT_STYLES.indexOf(style) < 0) {
							pattern = style;
						} else {
							type = style;
						}
					}

					if (value == UNIQUE_KEY) {
						value = 0;
						bHash = true;
					}

					value = formatNumberWithStylePattern(value, siteLocale, pattern, type);

					if (bHash) {
						value = value.replace(/0/g, "#");
					}
				} else if ((type == TYPE_DATE) || (type == TYPE_TIME)) {
					const date = new Date(value);
					if (style != null) {
						if (DATE_FORMAT_STYLES.indexOf(style) < 0) {
							value = dateFormat(date, style);
						} else {
							if (type == TYPE_DATE) {
								value = new Intl.DateTimeFormat(siteLocale, {
									dateStyle: style as DateTimeStyle,
								})
									.format(date);
							} else if (type == TYPE_TIME) {
								value = new Intl.DateTimeFormat(siteLocale, {
									timeStyle: style as DateTimeStyle,
								})
									.format(date);
							}
						}
					} else {
						value = new Intl.DateTimeFormat(siteLocale)
							.format(date);
					}
				}
			}

			return value;
		});
	}

	//SERVER:String:leftstr
	leftstr(s: string, n: number): string {
		return s.substring(0, n);
	}

	//SERVER:String:length
	length(s: any): number | undefined {
		return String(s).length;
	}

	//SERVER:String:lowercase
	lowercase(s: string): string {
		return s.toLowerCase();
	}

	//SERVER:String:reContains
	reContains(s: string, re: string): boolean {
		return String(s)
			.match(re) != null;
	}

	//SERVER:String:reMatch
	reMatch(s: string, re: string): boolean {
		if (re.charAt(0) != "^") {
			re = "^" + re;
		}

		if (re.charAt(re.length - 1) != "$") {
			re += "$";
		}

		const str = String(s);
		const result = str.match(re);
		const last = Math.max(str.length - 1, 0);

		return result != null && result.index <= last;
	}

	//SERVER:String:replace
	replace(s: string, s1: string, s2: string): string {
		return s ? s.split(s1)
			.join(s2) : s;
	}

	//SERVER:String:rightstr
	rightstr(s: string, n: number): string {
		return s.substring(s.length - n);
	}

	//SERVER:String:split
	split(str: string, separator: string): string[] {
		return str.split(separator);
	}

	//SERVER:String:substr
	substr(s: string, n1: number, n2: number): string {
		return s.substring(n1, n2);
	}

	//SERVER:String:uppercase
	uppercase(s: string): string {
		return s.toUpperCase();
	}

	//Type functions

	//SERVER:Type:date
	cnl(url: string): string {
		return String(url);
	}

	//SERVER:Type:date
	date(time: TimeValue): number {
		if (typeof time == "number") {
			return time;
		}

		return new Date(time)
			.getTime();
	}

	//SERVER:Type:double
	double(v: any): number {
		return Number(v);
	}

	//SERVER:Type:enum
	_enum(id: string, v: string): string {
		if (id && (id.indexOf(":") > 0)) {
			console.assert(v == null, "Rollup: enum id has colon with non-null value - " + id + ", " + v);
			return id;
		}

		return xml.enumCreate(id, v);
	}

	//SERVER:Type:enumName
	enumName(v: string): string {
		return xml.enumGetId(v);
	}

	//SERVER:Type:enumValue
	enumValue(v: string): string {
		return xml.enumGetText(v);
	}

	//SERVER:Type:float
	float(v: any): number {
		return Number(v);
	}

	//SERVER:Type:integer
	integer(v: number): number {
		return Math.floor(v);
	}

	//SERVER:Condition:ifNull
	isNull(x: any): boolean {
		return x == null || x == csi.EMPTY_URL;
	}

	//SERVER:Condition:isOfType
	isOfType(v: any, type: string): boolean {
		switch (type) {
			case "array":
			case "vector":
			case "set":
			case "list": return v instanceof Array;
			case "map": return _.isObject(v);

			case xml.ATTR_BOOLEAN: return (v === true) || (v === false);
			case xml.ATTR_ENUM: return !!reflection.getEnumList(xml.enumGetId(v));
			case xml.ATTR_REF: return xml.isValidURL(v);
			case xml.ATTR_STRING: return typeof v === "string";

			case "number":
			case xml.ATTR_DOUBLE:
			case xml.ATTR_FLOAT: return _.isNumber(v);

			case xml.ATTR_INTEGER: return _.isNumber(v) && (Math.round(v) == v);

			// ???
			case "matrix":
			case "date": return false;
		}

		return false;
	}

	//SERVER:Condition:list
	list(): any[] {
		return _argumentsToArray(arguments);
	}

	//SERVER:Condition:map
	map(): any {
		const m = {};
		const x = arguments[0];
		if ((typeof x === "string") && !x.indexOf("class:")) {
			return m;
		}
		for (var i = 0; i < arguments.length - 1; i += 2) {
			m[arguments[i]] = arguments[i + 1];
		}
		return m;
	}

	//SERVER:Condition:ref
	ref(v: any): cnl {
		return String(v);
	}

	//SERVER:Condition:set
	set(): any[] {
		return this.unique(_argumentsToArray(arguments));
	}

	//SERVER:Condition:string
	string(v: any): string {
		return String(v);
	}

	//SERVER:Condition:vector
	vector(): any[] {
		return _argumentsToArray(arguments);
	}

	// Other functions
	/**
	* Return node given its URL; if url is given and node is not in the cache, then throw error
	*/
	_getNode(url: string): C8Node {
		const node = resolver.resolveNode(url, this.forceLoadMissingNodes, this.cache) || reflection.getObject(url);
		if (node) {
			return node;
		}

		if (xml.isValidURL(url)) {
			throw new Error(url);
		}

		return null;
	}

	/*
	* Return values for each node's attribute; return null if any nodes missing
	*/
	_getValues(a: string[], id: string): any[] {
		if (!a.length || (typeof a[0] !== "string")) return a;

		if (typeof id === "string") {
			return _.map(a, (url) =>
				this.attr(url, id));
		}

		throw new Error("Rollup function not supported");
	}

	/**
	 * Return value for given id which could either be a variable or attribute; variables take precedence
	 */
	getVariable(id: string): any {
		const value = this.variables[id];
		return isNullOrUndefined(value) ? this.attr(id) : value;
	}

	/**
	 * Set value for variable
	 */
	setVariable(id: string, value: any): any {
		this.variables[id] = value;
		return value;
	}
}

export function setServerTimeZoneOffset(timeZoneOffset: number): void {
	serverTimeZoneOffset = timeZoneOffset;
}

export default ServerRollupScope;